/**
 * Last-touch marketing attribution per store: UTM tags + the external referrer hostname, captured when a
 * visit starts, kept 7 days in localStorage (survives logout) and sent with the checkout.
 */

export type Attribution = {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  referrerHost?: string
  capturedAt: string
}

export const ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000
const STORAGE_PREFIX = "meucontroleai:attribution:"
const MAX_TAG_LENGTH = 100
const UTM_KEYS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
} as const

type UtmField = keyof typeof UTM_KEYS

function cleanTag(raw: string | null, lower: boolean): string | undefined {
  if (!raw) return undefined
  const printable = Array.from(raw, (ch) => {
    const code = ch.charCodeAt(0)
    return code < 32 || code === 127 ? " " : ch
  }).join("")
  const collapsed = printable.replace(/\s+/g, " ").trim().slice(0, MAX_TAG_LENGTH)
  if (!collapsed) return undefined
  return lower ? collapsed.toLowerCase() : collapsed
}

/** External referrer hostname only (never path or query); empty for same-site or unparsable referrers. */
export function externalReferrerHost(referrer: string, currentHost: string): string | undefined {
  if (!referrer) return undefined
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, "")
    const current = currentHost.toLowerCase().replace(/^www\./, "")
    if (!host || host === current) return undefined
    return host.slice(0, MAX_TAG_LENGTH)
  } catch {
    return undefined
  }
}

/** Reads the UTM tags of a query string; source/medium lower-cased, every tag trimmed and capped. */
export function readUtm(search: string): Partial<Record<UtmField, string>> {
  const params = new URLSearchParams(search)
  const out: Partial<Record<UtmField, string>> = {}
  for (const [field, key] of Object.entries(UTM_KEYS) as [UtmField, string][]) {
    const value = cleanTag(params.get(key), field === "utmSource" || field === "utmMedium")
    if (value) out[field] = value
  }
  return out
}

const CAMPAIGN_CODE = /^[a-z0-9]{3,12}$/

/** The short-link code of `?mc=`, lower-cased; undefined when absent or malformed. */
export function readCampaignCode(search: string): string | undefined {
  const raw = cleanTag(new URLSearchParams(search).get("mc"), true)
  return raw && CAMPAIGN_CODE.test(raw) ? raw : undefined
}

/** Tags of a visit: UTM params; a `?mc=` code defaults to WhatsApp campaign traffic (explicit source/medium/campaign/term win) and is always `utmContent`. */
export function readTouchTags(search: string): Partial<Record<UtmField, string>> {
  const code = readCampaignCode(search)
  const utm = readUtm(search)
  if (!code) return utm
  return { utmSource: "whatsapp", utmMedium: "campaign", ...utm, utmContent: code }
}

/** The `?mc=` short-link code of the stored touch (kept as `utmContent`), or undefined when none is stored in the window. */
export function storedCampaignCode(merchantId: string | undefined | null, now: number = Date.now()): string | undefined {
  const content = getAttribution(merchantId, now)?.utmContent?.toLowerCase()
  return content && CAMPAIGN_CODE.test(content) ? content : undefined
}

function storageKey(merchantId: string): string {
  return `${STORAGE_PREFIX}${merchantId}`
}

/** The stored attribution when still inside the window, otherwise null. */
export function getAttribution(merchantId: string | undefined | null, now: number = Date.now()): Attribution | null {
  if (!merchantId) return null
  try {
    const raw = localStorage.getItem(storageKey(merchantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Attribution
    const at = Date.parse(parsed.capturedAt)
    if (!Number.isFinite(at) || now - at > ATTRIBUTION_WINDOW_MS) {
      localStorage.removeItem(storageKey(merchantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * Captures the touch that started this visit: a URL with UTM tags or an external referrer replaces the stored
 * attribution (last touch); a direct visit keeps the stored one inside its window.
 */
export function captureAttribution(
  merchantId: string | undefined | null,
  location: { search: string; hostname: string } = window.location,
  referrer: string = document.referrer,
  now: number = Date.now(),
): Attribution | null {
  if (!merchantId) return null
  const utm = readTouchTags(location.search)
  const referrerHost = externalReferrerHost(referrer, location.hostname)
  if (Object.keys(utm).length === 0 && !referrerHost) return getAttribution(merchantId, now)
  const touch: Attribution = { ...utm, ...(referrerHost ? { referrerHost } : {}), capturedAt: new Date(now).toISOString() }
  try {
    localStorage.setItem(storageKey(merchantId), JSON.stringify(touch))
  } catch {
    return touch
  }
  return touch
}

/** Attribution sent with a checkout: the stored touch, else the UTM tags still present on the current URL. */
export function checkoutAttribution(
  merchantId: string | undefined | null,
  location: { search: string; hostname: string } = window.location,
  now: number = Date.now(),
): Attribution | null {
  const stored = getAttribution(merchantId, now)
  if (stored || Object.keys(readTouchTags(location.search)).length === 0) return stored
  return captureAttribution(merchantId, location, "", now)
}
